import { Box } from "@mui/joy";
import type { CriteriaSummary } from "components/shared/followup/helpers/followupSummaryHelpers";
import { getColorForCriterion } from "components/shared/followup/helpers/followupSummaryHelpers";
import type { Comment } from "../../../../../api/zekay/comments";
import DarkTypography from "../../../../common/DarkTypography";
import { colorTokens, textTokens, withAlpha } from "../../../../../styles/globalStyles";

const modulesStyles = {
  moduleContainer: {
    marginBottom: 4,
  },
  moduleHeader: {
    marginBottom: 1,
    marginTop: 2,
    color: textTokens.color.title,
    fontWeight: "bold",
  },
  gridContainer: {
    display: "grid",
    gridTemplateColumns: "2% 20% 20% 30%",
    gap: 1,
    padding: 1,
  },
  gridTypographyTitle: {
    color: textTokens.color.bodyOnLight,
    fontWeight: "bold",
    fontSize: "clamp(0.7rem, 2vw, 1rem)",
  },
  gridTypography: {
    color: textTokens.color.bodyOnLight,
    fontSize: "clamp(0.7rem, 2vw, 1rem)",
  },
};

type SummaryModuleBlockProps = {
  moduleName: string;
  criteria: CriteriaSummary[];
};

export default function SummaryModuleBlock({ moduleName, criteria }: SummaryModuleBlockProps) {
  return (
    <Box key={moduleName}>
      <DarkTypography sx={modulesStyles.moduleHeader}>{moduleName}</DarkTypography>
      <Box sx={modulesStyles.gridContainer}>
        <Box />
        <DarkTypography sx={modulesStyles.gridTypographyTitle}>Description</DarkTypography>
        <DarkTypography sx={modulesStyles.gridTypographyTitle}>Critères</DarkTypography>
        <DarkTypography sx={modulesStyles.gridTypographyTitle}>Commentaires</DarkTypography>
      </Box>

      {criteria.map((criterion, idx) => (
        <Box
          key={idx}
          sx={{
            ...modulesStyles.gridContainer,
            borderBottom: "1px solid #D8D8D8",
          }}
        >
          <Box
            sx={{
              borderLeft: `5px solid ${getColorForCriterion(criterion)}`,
            }}
          />
          <DarkTypography sx={modulesStyles.gridTypography}>{criterion.description}</DarkTypography>
          <DarkTypography sx={modulesStyles.gridTypography}>
            {criterion.status.split("\n").map((line: string, index: number) => {
              if (line.includes("Validé :")) {
                return (
                  <span key={index}>
                    <strong style={{ color: colorTokens.status.success }}>
                      {line.split(":")[0]}:
                    </strong>{" "}
                    {line.split(":")[1]}
                    <br />
                  </span>
                );
              }
              if (line.includes("Manquant :")) {
                return (
                  <span key={index}>
                    <strong style={{ color: colorTokens.status.warning }}>
                      {line.split(":")[0]}:
                    </strong>{" "}
                    {line.split(":")[1]}
                    <br />
                  </span>
                );
              }
              return (
                <span key={index}>
                  {line}
                  <br />
                </span>
              );
            })}
          </DarkTypography>
          <Box sx={modulesStyles.gridTypography}>
            {criterion.comments.length > 0 && (
              <ul
                style={{
                  listStyleType: "none",
                  padding: 0,
                  margin: 0,
                }}
              >
                {criterion.comments.map((comment: Comment, cIdx: number) => (
                  <li
                    key={cIdx}
                    style={{
                      marginBottom: "10px",
                      padding: "8px",
                      backgroundColor: withAlpha(textTokens.color.inverse, 0.06),
                      borderRadius: "4px",
                    }}
                  >
                    <Box>
                      <DarkTypography level="body-sm" fontWeight="bold" sx={{ mb: 0.5 }}>
                        {comment.user} -{" "}
                        {new Date(comment.timestamp).toLocaleString("fr-FR", {
                          day: "2-digit",
                          month: "2-digit",
                          year: "numeric",
                          hour: "2-digit",
                          minute: "2-digit",
                        })}{" "}
                        :
                      </DarkTypography>
                      <DarkTypography level="body-sm" sx={{ ml: 1 }}>
                        {comment.comment}
                      </DarkTypography>
                    </Box>
                  </li>
                ))}
              </ul>
            )}
          </Box>
        </Box>
      ))}
    </Box>
  );
}
